























































































































import {Vue, Component} from 'vue-property-decorator'
import {Action, State} from 'vuex-class'

import {ProfileState} from '@/store/modules/profile'
import {BookingState} from '@/store/modules/booking'
import {VehicleSlotState} from '@/store/modules/vehicle'
import {AppConfigState} from '@/store/modules/configuration'

import {EventBus} from '@/main'
import Locate, {Position} from '@/lib/location'
import {ParkingLot, Poi, ReservationResponse, Position as Pos} from '@/lib/kepler/interfaces'
import ServiceMesh from '@/lib/serviceMesh'
import Utils from '@/utils'

import MapStyle from '../lib/map/styles/black.js'
import GoogleMap from '../lib/map/GoogleMap.vue'
import GoogleMapCircle from '../lib/map/GoogleMapCircle.vue'
import GoogleMapMarker from '../lib/map/GoogleMapMarker.vue'
import GoogleMapPolygon from '../lib/map/GoogleMapPolygon.vue'
import GoogleMapPolyline from '../lib/map/GoogleMapPolyline.vue'
import GoogleMapMarkerCluster from '../lib/map/GoogleMapMarkerCluster.vue'
import {AxiosPromise} from 'axios'
import {MapState} from '@/store/modules/map'

interface MarkerLabel {
  color?: string
  fontFamily?: string
  fontSize?: string
  fontWeight?: string
  text: string
}

interface ItemPosition {
  lat: number
  lng: number
}

interface Item {
  item: ReservationResponse
  position: ItemPosition
}

@Component({
  components: {
    VehicleCardPopup: Utils.loadComponent('entities/vehicle/VehicleCardPopup'),
    CircleToggle: Utils.loadComponent('CircleToggle'),
    TopBar: Utils.loadComponent('TopBar'),
    MapFilters: Utils.loadComponent('MapFilters'),
    Button: Utils.loadComponent('Button'),
    CustomIcon: Utils.loadComponent('CustomIcon'),
    Flex: Utils.loadComponent('proxy/Flex'),
    Alert: Utils.loadComponent('proxy/Alert'),
    ParkingCardPopup: Utils.loadComponent('entities/vehicle/ParkingCardPopup'),
    ProgressCircular: Utils.loadComponent('proxy/ProgressCircular'),
    Layout: Utils.loadComponent('proxy/Layout'),

    GoogleMap,
    GoogleMapPolygon,
    GoogleMapMarker,
    GoogleMapPolyline,
    GoogleMapMarkerCluster,
    GoogleMapCircle,
  },
})
export default class Map extends Vue {
  // Store mappings
  @State('profile') public profileState!: ProfileState
  @State('vehicle') public vehicleState!: VehicleSlotState
  @State('configuration') public configState!: AppConfigState
  @State('booking') public bookingState!: BookingState
  @State('map') public mapState!: MapState

  @Action('openPopup') public openPopup: any
  @Action('bookingSearch') public bookingSearch: any
  @Action('bookingFlush') public bookingFlush: any
  @Action('setUserPosition') public setUserPosition: any
  @Action('getParkingLots') public getParkingLots!: (position: Pos) => AxiosPromise<ParkingLot[]>

  public gpsError: boolean = false
  public toggles: boolean[] = []
  public layerMarkers: any[] = []
  public POI: Poi[] = []

  public centerPoint: Position = {
    lat: 0,
    lng: 0,
    acc: null,
  }
  public showMarker: boolean = false

  public showParkingZones: boolean = false
  public showParkingLots: boolean = false
  public loading: boolean = false

  // Computed properties
  public get alertMessage() {
    if (!this.profileState.driver) {
      return this.$t('no-login')
    }
    return this.centerPoint.acc == null ? this.$t('no-gps-lock') : null
  }

  public get activeReservationIcon() {
    return 'img/map/markers/POINTER.svg'
  }

  public get activeReservations() {
    const reservations = this.bookingState.activeReservations
    const items: Item[] = []

    reservations.forEach((reservation: ReservationResponse, k: number) => {
      items.push({
        item: reservation,
        position: {
          lat: reservation.vehicle_slot.position.latitude,
          lng: reservation.vehicle_slot.position.longitude,
        },
      })
    })
    return items
  }

  public get mapStyle() {
    // playApp styleTest3 semiClassico
    const style = this.profileState.mapStyle ? this.profileState.mapStyle : 'semiClassico'
    return MapStyle.getStyle(style)
  }

  protected get activeReservationTypes(): string[] {
    const types: string[] = []
    const res = this.bookingState.activeReservations
    if (res) {
      res.forEach((r) => {
        const bookingMode = r.type
        const vehicleType = r.vehicle_slot.vehicle.category.type
        if (bookingMode && vehicleType) {
          types.push(bookingMode + vehicleType.toUpperCase(),
          )
        }
      })
    }
    return types
  }

  protected get mapRef() {
    return this.$refs.map as any
  }

  protected get clusteredPOI() {
    return !this.POI.length ? null : this.POI.filter((p) => {
      return p.visibility === 'normal'
    })
  }

  protected get unclusteredPOI() {
    return !this.POI.length ? null : this.POI.filter((p) => {
      return p.visibility === 'important'
    })
  }

  protected setShowParkingZones(zones: boolean, lots: boolean) {
    this.showParkingZones = zones
    this.showParkingLots = lots
    this.loading = false
  }

  protected getPolyLineColor(zone: string) {
    return ServiceMesh.getPolyLineColor(zone)
  }

  protected setLayersMarkers(markers: any) {
    this.layerMarkers = markers
    this.mapRef.getBounds(markers.slice(0, 3), this.centerPoint)
    this.loading = false
  }

  protected getPark = () => {
    this.getParkingLots({
      latitude: this.centerPoint.lat,
      longitude: this.centerPoint.lng,
    })
  }

  protected centerMap() {
    const mapRef = this.mapRef
    if (mapRef) {
      mapRef.pan(this.centerPoint.lat, this.centerPoint.lng)
    }
    EventBus.$emit('recenter')
  }

  protected setupMap() {
    this.loading = true
    Locate.locate((pos) => {
        this.centerPoint = pos
        this.setUserPosition(pos)
        this.gpsError = false
        this.centerMap()
      },
      this.centerPoint, () => {
        this.gpsError = true
        this.loading = false
      })
    this.getPark()
  }

  protected parkingMarkerLabel(m: any): { label: MarkerLabel | string, origin: [number, number] } {
    let available = m.parking_slots_availability.available
    available = available || ''
    const color = this.$vuetify.theme.PARKING ? this.$vuetify.theme.PARKING.toString() : ''
    return available ? {
      label: {
        text: available.toString(),
        fontSize: '10px',
        fontWeight: '600',
        color,
      },
      origin: [23, 23.4],
    } : {
      label: {
        text: ' ',
      },
      origin: [15, 15],
    }
  }

  protected setPOI(p: Poi[]) {
    this.POI = p
  }

  // Lifecycle hooks
  private mounted() {
    this.centerPoint = {
      lat: this.configState.appConfig.default_latitude,
      lng: this.configState.appConfig.default_longitude,
      acc: null,
    }

    this.setupMap()

    EventBus.$on('cron1', this.getPark)
  }

  private destroyed() {
    EventBus.$off('login', this.setupMap)
    EventBus.$off('cron1', this.getPark)
  }

}
